import { queryOptions, useQuery } from '@tanstack/react-query';

import { EdgeInitdOnboardingConfig } from '@cast/types-omni';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const OMNI_EDGE_INITD_ONBOARDING_SCRIPT_QUERY_KEY =
  'omni-edge-initd-onboarding-script';

const getEdgeInitdOnboardingScriptQueryOptions = ({
  organizationId,
  clusterId,
  edgeLocationId,
  config,
  enabled,
}: {
  organizationId: string | undefined;
  clusterId?: string;
  edgeLocationId?: string;
  config: EdgeInitdOnboardingConfig;
  enabled: boolean;
}) =>
  queryOptions({
    // Serialize config to a stable string to avoid referential equality misses in TanStack Query
    queryKey: [
      OMNI_EDGE_INITD_ONBOARDING_SCRIPT_QUERY_KEY,
      organizationId,
      clusterId,
      edgeLocationId,
      JSON.stringify(config),
    ] as const,
    queryFn: async () => {
      const { data } = await apiClient.omni.onboardEdgeInitd({
        organizationId: organizationId!,
        clusterId: clusterId!,
        id: edgeLocationId!,
        edgeInitdOnboardingConfig: config,
      });

      return data.script ?? null;
    },
    enabled: makeIsEnabled(
      !!organizationId && !!clusterId && !!edgeLocationId,
      enabled
    ),
  });

export const useGetEdgeInitdOnboardingScriptQuery = ({
  clusterId,
  edgeLocationId,
  config,
  enabled = true,
}: {
  clusterId?: string;
  edgeLocationId?: string;
  config: EdgeInitdOnboardingConfig;
  enabled?: boolean;
}) => {
  const { currentOrganization } = useOrganizations();

  const {
    data: script,
    isLoading,
    error,
  } = useQuery(
    getEdgeInitdOnboardingScriptQueryOptions({
      organizationId: currentOrganization?.id,
      clusterId,
      edgeLocationId,
      config,
      enabled,
    })
  );

  return { script, isLoading, error };
};
